import { MutationCache, QueryClient } from '@tanstack/react-query';

declare module '@tanstack/react-query' {
  interface Register {
    mutationMeta: {
      /**
       * Opt out of the global failure toast — for a mutation whose caller shows
       * the failure itself (inline, or with its own toast at the call site).
       */
      silent?: boolean;
    };
  }
}

type ErrorReporter = (error: unknown) => void;
let reportMutationError: ErrorReporter | null = null;

/**
 * Where the global fallback sends a failure. Set by a component mounted inside
 * the snackbar provider — the client is created at module scope, before there
 * is any React tree to toast from.
 */
export function setMutationErrorReporter(reporter: ErrorReporter | null): void {
  reportMutationError = reporter;
}

/**
 * One retry for what might be transient — a dropped connection, a 5xx — and
 * none for a 4xx. "Not found", "not allowed" and "switched off here" are
 * answers, not hiccups: asking again only doubled every such request, and a
 * file page whose panels each got a 404 sent twice the traffic to learn it.
 */
export function shouldRetryQuery(failureCount: number, error: unknown): boolean {
  const status = (error as { response?: { status?: number } } | null)?.response?.status;
  if (typeof status === 'number' && status >= 400 && status < 500) return false;
  return failureCount < 1;
}

export const queryClient = new QueryClient({
  defaultOptions: {
    queries: { staleTime: 30_000, retry: shouldRetryQuery, refetchOnWindowFocus: false },
  },
  /**
   * The safety net under every write.
   *
   * A mutation hook with no `onError` used to fail in silence: the button
   * re-enabled and nothing else happened, which an officer reads as "it worked".
   * Hooks that handle their own failure — an `onError`, or `meta.silent` when
   * the screen shows it inline — are left alone, so nothing is said twice.
   */
  mutationCache: new MutationCache({
    onError: (error, _variables, _context, mutation) => {
      if (mutation.options.onError || mutation.options.meta?.silent) return;
      reportMutationError?.(error);
    },
  }),
});
